import { DocumentFiles } from "domain/entities/document-files";
import  DocumentFilesRepository  from "infra/repositories/document-files-repository";
import { useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import { DocumentFilesInputFilters } from "domain/models/document-files-input-filter";
import { Logger } from "infra/logger/logger";
import { AxiosError } from "axios";
import { DocumentFilesPayload, UpdateInput } from "domain/entities/document-files";

export const useDocumentFilesByFilter = (filter: DocumentFilesInputFilters) => {
  const logger = Logger.getInstance();

  return useQuery({
    queryKey: ["documentfiles", filter],
    queryFn: async () => {
        try {
            const result = await DocumentFilesRepository.getAll(filter);
            return result;
        } catch (error: any) {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
            );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    },
    retry: 1,
    staleTime: 0,
});
};

export const useDocumentFilesById = (id: string) => {
    const logger = Logger.getInstance();

    return useQuery({
        queryKey: ["documentfiles", id],
        queryFn: async () => {
            try {
                if (!id) throw new Error("ID inválido");
                const result = await DocumentFilesRepository.getById(id);
                return result;
            } catch (error: any) {
                if (error instanceof AxiosError) {
                logger.error(error.response?.data.metadata.errors[0],
                error.response?.data?.metadata
                );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    },
    enabled: !!id,
    retry: 1,
    staleTime: 0,
});
};

export const useCreateDocumentFilesMutation = () => {
    const logger = Logger.getInstance();
    const queryDocumentFiles = useQueryClient();

    return useMutation({
        mutationFn: async (files: DocumentFilesPayload) => {
            const result = await DocumentFilesRepository.create(files);
            return result;
        },
        onSuccess: () => {
            queryDocumentFiles.invalidateQueries({ queryKey: ["documentfiles"] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                if (error.response?.data?.metadata?.errors) {
                    const errors = error.response.data.metadata.errors;
                    logger.error(errors[0], error.response?.data.metadata);
                    console.log("error axios",errors[0]);
                    const customError = new Error("Erro ao criar um arquivo do documento");
                    (customError as any).errors = errors;
                    throw customError;
                } else {
                    throw new Error("Erro desconhecido da API");
                }
            } else {
                logger.error("Erro desconhecido", error);
                throw error;
            }
        }
    });
};

export const useDeleteDocumentFilesMutation = () => {
    const logger = Logger.getInstance();
    const queryDocumentFiles = useQueryClient();

    return useMutation({
        mutationFn: async (id: string) => {
            if (!id) throw new Error("ID inválido para exclusão");
            const result = await DocumentFilesRepository.delete(id);
            return result;
        },
        onSuccess: () => {
            queryDocumentFiles.invalidateQueries({
                queryKey: [
                "documentfiles",
            ] });
        },
        onError: (error: any) => {
            if (error instanceof AxiosError) {
                logger.error(
                    error.response?.data.metadata.errors[0],
                    error.response?.data.metadata
            );
                throw new Error(error.response?.data.metadata.errors);
            }
            throw error;
        }
    });
};

export const useUpdateDocumentFilesMutation = () => {
  const logger = Logger.getInstance();
  const queryDocumentFiles = useQueryClient();

  return useMutation({
    mutationFn: async (newdata: UpdateInput) => {
      const result = await DocumentFilesRepository.update(newdata);
      return result;
    },
    onSuccess: () => {
      queryDocumentFiles.invalidateQueries({ queryKey: ["documentfiles"] });
    },
    onError: (error: any) => {
      if (error instanceof AxiosError) {
        logger.error(
          error.response?.data.metadata?.errors?.[0],
          error.response?.data.metadata
        );
        throw new Error(error.response?.data.metadata?.errors);
      }
      throw error;
    },
  });
};